import React from 'react';
import { theme } from '../theme';
import { declineInsteadWord, declineInsteadHue, DECLINE_LABEL } from '../services/declineVerdict';
import { showsKopiert, rowClaimVia } from '../services/settledRow';
import { copyToastMessage } from '../words';
import type { TicketAttributeValues, SenderOrganisationRef, TicketDecline } from '../serverTypes';
import { useCategoryMeta } from '../context/ClassificationSchemaContext';
import { SecondaryAxisPills, pillFace, pillStyle, type SecondaryAxisLimit } from './SecondaryAxisPills';
import { DecisionBadge, DecisionBadges, approvedRowKinds } from './DecisionBadge';
import { FristGenanntPill } from './FristGenanntPill';
import { FormPill } from './FormPill';
import { FlagPill, orderedFlags } from './FlagPill';
import { InfoTooltip } from './InfoTooltip';
import { TRANSIENT_STATUSES } from '../hooks/useTicketDetailRefresh';
import { useRowPillVisibility } from '../hooks/useRowPillVisibility';
import { visibleFlags } from '../services/impliedSignals';
import { ClerkTrace } from './ClerkTrace';
import { PaperclipIcon } from './icons';
import { AttachmentGlyphSlot, mayShowAttachmentGlyph, shouldShowAttachmentGlyph } from './AttachmentGlyphSlot';
import { CategoryBadge } from './CategoryBadge';
import { SenderPill, useSenderRegistryServed } from './SenderPill';
import { Spinner } from './Spinner';
import { StufeChip } from './StufeChip';
import { TicketAttributePills } from './TicketAttributePills';
import { handlingTooltipText, hasSomethingToSay } from './HandlingStripFace';

// ─── TicketRowMeta ──────────────────────────────────────
// <purpose>Rows 2+3 of a ticket-list row, shared across Antworten/Archiv so the layout can't
// drift. ROW COMPOSITION v1 (part 7 fix loop, 2026-10-12, operator rulings from the composition
// board) — the house rule, verbatim in smarttix-receiver.md:
//   Row 2 left  — the primary axis (in-strip label or badge, the tenant's UI_ROW_LAYOUT switch) ·
//     the pending chip (no category yet) · the secondary-axis pills · the paperclip · the
//     Neu-Analyse count (a note on the classification).
//   Row 3 left  — the TENANT-EXCLUSIVE plum pill FIRST when present (SenderPill on a
//     sender-registry tenant; FormPill „Formular" on a form-extraction tenant when the mail carries
//     a recognised form; nothing yet elsewhere) · then the info pills: flags · Frist genannt · Stufe n ·
//     the utility exception pill („Gas").
//   Row 3 right — the Bearbeitung group: the approved-as badge (DecisionBadges) · Kopiert ·
//     Bearbeitet · Eingang bestätigt.
// THE DATES ARE GONE from both lines (R33): the arrival is the row-1 age the PAGES mount beside the
// unread dot (ArrivalAge — part 7 fix loop, 2026-10-12: the one clock), the decision date lives on
// the detail's decision line.</purpose>
// <constraints>Row 3 renders on EVERY row at row 2's own minHeight (18) — pre-allocated, so a row
// with no plum pill, no info pill and no Bearbeitung stands as tall as a full one (Layout-Stability).
// The Bearbeitung group uses marginLeft:auto — no fixed widths; a badge or trace landing grows it
// leftward into free space and pushes nothing. The left groups are `display: contents` wrappers
// (testids + order guards, no box), so an empty group occupies nothing and leaves no artefact.
// maxSecondaryAxisVisible is caller-supplied (the per-page pill cap differs).
// strip_abbr (Kürzel im Streifen) is rendered by the caller inside the colour strip, not here —
// in BOTH branches since 2026-09-09 (see categoryBadge for which word the badge wears).
// compact (Archiv ledger): ONE meta line carrying the SAME three groups in the same order; the
// ledger's own visible date is the ARCHIVE stamp, on the caller's subject line.
// Pending chip: the BE bucket boundary lets 'categorizing'/'categorized'/'generating_suggestion'
// tickets (category NULL, no suggestion yet) into the same buckets that already carry 'reanalyzing'
// — TRANSIENT_STATUSES (useTicketDetailRefresh) is reused rather than a second status list. Shown
// ONLY when there's no category yet, so an in-progress reanalysis (which keeps its prior category)
// renders exactly as before.</constraints>
export const TicketRowMeta: React.FC<{
  // The attribute pair rides in through the shared shape (types.ts) rather than as two more
  // hand-listed optional fields: this prop is a structural double of a list row, and the ONE
  // declaration is what keeps the double from drifting from the DTO it stands in for.
  ticket: TicketAttributeValues & {
    primaryAxis?: string;
    // Drives the pending chip only — absent/unrecognized status is simply never pending
    // (fail-open, same posture as every other optional field here).
    status?: string;
    secondaryAxis?: string[];
    hasAttachments?: boolean;
    // Documents only, per the ingest stamp — see shouldShowAttachmentGlyph above for how this and
    // hasAttachments combine under the tenant's counting rule.
    documentAttachmentCount?: number;
    reanalysisCount?: number;
    // The two DECISION stamps a list payload carries. NEITHER is read as a date here any more
    // (R33: no row states one) — `copyApprovedAt` is read on its own by the right group's
    // „Kopiert" badge, which states the GESTURE, and `approvedAt` is carried for the callers that
    // age a row by it. Whether a row is DECIDED is read off the claims (decisionKindsOf, via
    // DecisionBadges), never off these: an ack-only row carries a stamp and is not decided.
    approvedAt?: string | null;
    copyApprovedAt?: string | null;
    edited?: boolean;
    eskalationsstufe?: number | null;
    // Flag ids raised on the row (TicketListItem.flags). Additive: absent on a pre-flag row, which
    // renders no pill.
    flags?: string[];
    // The letter check's first deadline quote (TicketListItem.deadlineQuote): „Frist genannt".
    deadlineQuote?: string | null;
    // Which draft kinds this row already carries a claim for — resolution pills, in served order
    // (additive — absent on pre-claim-family backends, rendered in BOTH branches once core serves it
    // to Archiv rows too). string[], not DraftKind[] — same posture as `flags` above: the
    // wire is untyped past the network boundary, and the unknown-kind guard below is only a real
    // guard if the type it narrows admits a value the served decisionKindHandling doesn't recognise.
    draftClaims?: string[] | null;
    // Presence of a recognised form (TicketListItem.hasFormExtraction) — the row half of the
    // „Formular" pill's gate; the tenant half is the served capability (cm.formExtractionEnabled).
    hasFormExtraction?: boolean;
    // The standing „Nicht hilfreich" verdict (TicketListItem.decline, core mig 090) — an Archiv-row
    // fact (a declined row is always filed), rendered as the Bearbeitung group's verdict pill:
    // „Stattdessen: <word>" when the clerk said what they did instead, „Nicht hilfreich" otherwise.
    decline?: TicketDecline | null;
    // Sender-identity registry (P2, 2026-09-13) — additive, absent on every pre-registry row and on
    // a sender with no matched organisation. Renders as SenderPill, leading row 3 (R33).
    senderOrganisation?: SenderOrganisationRef | null;
    customerEmail?: string | null;
    // NOTE `draftClaimVia` (kind -> "approve" | "copy") is served but DELIBERATELY not read here
    // since R32 (2026-09-18): the decision badge states the approved handling's own label,
    // and a decision reads the same whichever gesture made it. Row 3's „Kopiert" badge is where
    // the copy gesture is stated — once, off `copyApprovedAt`, never re-derived from the claim map.
  };
  maxSecondaryAxisVisible: SecondaryAxisLimit;
  compact?: boolean;
  // Tenant-wide inline-image counting rule (UI_ATTACHMENTS_COUNT_INLINE). Defaults true so every
  // existing test double / call site written before this prop existed keeps today's exact glyph
  // behaviour (hasAttachments alone) rather than silently flipping posture.
  attachmentsCountInline?: boolean;
}> = ({ ticket: t, maxSecondaryAxisVisible, compact = false, attachmentsCountInline = true }) => {
  const cm = useCategoryMeta();
  // Read HERE as well as inside the pill: row 3 must decide whether there is a LEFT GROUP at all,
  // and a wrapper rendered around a component that answers null would be an empty flex item — the
  // reservation this row grammar exists to refuse.
  const senderRegistryServed = useSenderRegistryServed();
  // „Hinweise in der Liste" (P2 Task R4, 2026-09-18). Consulted per SIGNAL below; a hidden signal
  // renders NOTHING — no slot, no spacer. Outside App's provider (every pre-R4 test double) every
  // signal is visible, so this read is behaviour-neutral until a clerk or an admin says otherwise.
  // Which signals may be hidden at all is the registry's call, never this file's:
  // services/rowPillKinds, held to this row by components/__tests__/rowPillRegistryParity.test.tsx.
  const rowPills = useRowPillVisibility();
  // Everything on these rows that is not a classification speaks the INFO register (pill house
  // rule, operator ruling 2026-09-08, walk round 4): pending / reanalysis / kopiert / bearbeitet
  // all spread this, and their faces go through pillFace so the
  // Propercase voice is the register's decision and not a spelling each call site remembers.
  const pill: React.CSSProperties = pillStyle('info', 'row');
  // part 7 round 2, 2026-10-13: every served secondary code is drawn, on the normal row and the
  // compact Archiv line alike — a code the band makes redundant is a vocabulary defect, not a pill to hide.
  const secondaryAxis = t.secondaryAxis ?? [];
  const eskN = t.eskalationsstufe && t.eskalationsstufe >= 1
    ? (Math.min(Math.max(t.eskalationsstufe, 1), 3) as 1 | 2 | 3) : null;
  // Category as a badge — in BOTH branches only when the operator is on strip_label; strip_abbr
  // renders the Kürzel in the caller's own colour strip instead (Antworten always had one, and the
  // Archiv ledger grew one on 2026-09-09 — see below).
  // ⚠ compact used to be exempt from that rule ("the Archiv has no strip, so it always badges").
  // The exemption is retired by operator ruling (walk fix round 2, 2026-09-09): it put the served
  // handling word on the row twice — once as this badge and once as the resolution pill beside it,
  // which names the same handling — in every mode, because the mode could not reach this row.
  // Which WORD the badge wears is a second question, and it is decided by the role's own DEFAULT:
  //   default strip_label (vnb, and wim since vocabulary v7) — the served label. Long names are
  //     precisely why that is the role's default, and its rows are the ones the label was written for.
  // ⚠ wim CHANGED BRANCH on 2026-09-13 (vocabulary v7, operator ruling: strip_label is its default
  // now), so a wim ledger row at its default wears the served word here rather than the axis id.
  // That re-opens, latently, the doubling the 2026-09-09 ruling closed — a row carrying a resolution
  // pill would name the same handling twice — but core does not serve draftClaims on ARCHIVED rows
  // yet, so no ledger row shows both today. Left as the rule states it rather than re-keyed to a
  // second proxy: which face a wim ledger badge should wear now is an operator call on the
  // badge/pill register, and this rule still says exactly what it always said.
  //   default strip_abbr (gMSB/wMSB) — the axis ID. Such a role is only on strip_label because
  //     a clerk deliberately flipped it, and the short id is what the strip would have carried:
  //     the row states the axis without repeating the pill's served word.
  // The id is the honest short form here rather than cm.abbr: the Kürzel belongs to the STRIP's
  // idiom (it is sized for a 32px colour column), and a badge is not that column.
  // ⚠ SCOPED TO compact — the ruling named the Archiv row, and the active list was not part of it.
  // The same doubling is visible there (a flipped wim tenant's active row badges „Weiterleiten"
  // beside a resolution pill saying „Weiterleiten"), and this rule would close it identically; it
  // is deliberately NOT applied there without a ruling, because the active list is a surface the
  // walk did not cover. Recorded here rather than silently generalised.
  // `?? ''` on BOTH arms (never just the label one): the face is compared against the label by
  // hasSomethingToSay below, which wants a definite string — and the no-axis row this guards for
  // never renders a badge at all (the `t.primaryAxis &&` gate below).
  const badgeFace: string = compact && cm.rowLayoutDefault === 'strip_abbr'
    ? (t.primaryAxis ?? '')
    : cm.label(t.primaryAxis ?? '');
  // The hover (vocabulary v7, 2026-09-13) is on the OWN-MARKUP branch only: the CategoryBadge branch
  // below carries the identical bubble inside the component itself, and wrapping it here too would
  // nest two tooltips on one span. Same helper both ways, so the text cannot diverge — and it names
  // the VALUE, which matters most on the compact face that shows the short id: the badge stays short
  // and the hover is where the served word and sentence live.
  // …and only when there IS something to say (final review of v7, same date): the same
  // hasSomethingToSay predicate CategoryBadge uses, so a badge whose face is already the label and
  // whose value serves no hint stays a plain span rather than a tab stop carrying its own echo.
  const ownMarkupBadge = (axis: string) => (
    <span data-testid="category-badge" style={{ ...pill, fontWeight: 600, color: cm.swatch(axis).ink, backgroundColor: cm.swatch(axis).fill, border: cm.swatch(axis).border, boxSizing: 'border-box', display: 'inline-flex', alignItems: 'center', lineHeight: '14px' }}>
      {badgeFace}
    </span>
  );
  // compact (Archiv ledger fix, operator ruling B9, 2026-09-16, C3.4: "remove the hover-over from
  // the Archivseite") drops the badge's hover altogether, on BOTH branches, whatever
  // hasSomethingToSay would otherwise say — the ledger is a scanning surface, not a deciding one.
  // `compact` is TicketRowMeta's own Archiv signature (only ArchivSeite passes it), so it is also
  // the flag this suppression keys on; every other row (the active list, uncompact) is untouched.
  const categoryBadge = cm.rowLayout === 'strip_label' && t.primaryAxis && (cm.categoryLabelsFromData ? (
    !compact && hasSomethingToSay(cm, t.primaryAxis, badgeFace)
      ? <InfoTooltip text={handlingTooltipText(cm, t.primaryAxis)}>{ownMarkupBadge(t.primaryAxis)}</InfoTooltip>
      : ownMarkupBadge(t.primaryAxis)
  ) : (
    <CategoryBadge category={t.primaryAxis} style={{ fontSize: 10, padding: '1px 6px', flexShrink: 0 }} tooltip={!compact} />
  ));
  // Pending chip — see <constraints>. Only when there's genuinely no category yet; a ticket that
  // already has one (e.g. mid-reanalysis) keeps its existing categoryBadge treatment untouched.
  const pendingChip = !t.primaryAxis && !!t.status && TRANSIENT_STATUSES.has(t.status) && (
    <span data-testid="pending-chip" style={{ ...pill, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <Spinner size={10} />{pillFace('info', 'Wird verarbeitet…')}
    </span>
  );
  // The zone (GRÜN/GELB/ROT) chip lives on detail surfaces only (KI-Vorschlag, PopoutShell slim
  // header — via the ZoneChip component below) — one visual home for the zone signal, not
  // duplicated onto every list row. The model's one-line status is retired fleet-wide (part 6,
  // 2026-10-07): pills are facets, never model-written summaries.
  // The FLAG pill (part 5-2; part 7, 2026-10-11: one served word, no glyph, no age, on EVERY tab
  // and in the compact Archiv ledger row): a recorded signal, the first INFO pill on row 3 after
  // the tenant-exclusive plum pill (ROW COMPOSITION v1, 2026-10-12). It carries no hover: a list row
  // serves flag ids only (no quote), and an info pill hovers a served explanation or nothing (part 7
  // round 3, 2026-10-14). Nothing is reserved on an unflagged row. Never hideable by a
  // preference: a flag is a safety signal. Hidden ONLY when the visible band implies it (the
  // implication rule, part 7 fix loop, 2026-10-12 — e.g. a served implied landing: the band IS the flag).
  const flagPills = orderedFlags(visibleFlags(cm, t.flags ?? [], t.primaryAxis)).map(id => (
    <FlagPill key={id} id={id} />
  ));
  // THE secondary axis pill, ported to SecondaryAxisPills — see its own header for the ruling
  // (de-slugged-lowercase face, standard colouring, full proper-case hover, never abbreviated, no
  // role exceptions). This row used to run a three-tier fallback here that let a vocabulary
  // delivery jump the face straight from the de-slugged id to the full German label (the divergence
  // formerly logged as D-6) — that transitional behaviour ends here; the component always renders
  // the de-slugged face regardless of what the tenant's data source is.
  // TicketRowMeta, DashboardSeite, TicketDetail, and StatistikSeite now share this one
  // component. UploadsSeite still calls formatSecondaryAxisCode/getReasonCodeLabel directly for its own
  // INTERACTIVE (removable) reason-code chip — not this migration's scope, same boundary as
  // FilterPanel's filter bubble below (own markup/active-state, resolution accessors only).
  // ChatbotTestSeite's two reason-code chips are plain (non-interactive) display and simply were
  // not in this task's surface list — a real residual, not a deliberate boundary like the two above.
  const secondaryAxisPills = rowPills.isVisible('process_type')
    && <SecondaryAxisPills codes={secondaryAxis} maxVisible={maxSecondaryAxisVisible} variant="row" />;
  // The classified-alongside attributes (vocabulary v7, 2026-09-13) — see TicketAttributePills for
  // the suppression rule and the fixed slot order. Rendered in BOTH branches: unlike
  // the flag pill (an in-play signal the settled ledger row drops), the commodity and the
  // sender kind are permanent facts about the mail, and a ledger row that stopped stating them
  // would read as a different mail from the one the active list showed.
  const attributePills = <TicketAttributePills ticket={t} variant="row" visibility={rowPills} />;
  // Reserve only for rows that could get a glyph (mayShowAttachmentGlyph) — a row with no
  // attachments has nothing to reserve for, and giving it a slot would make settle collapse the
  // width AND its flex gap on the majority of rows.
  const attachment = mayShowAttachmentGlyph(t) && (
    cm.configLoading ? <AttachmentGlyphSlot size={13} /> : shouldShowAttachmentGlyph(t, attachmentsCountInline) && (
      <span data-testid="attachment-glyph" title="Anhang" style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
        <PaperclipIcon size={13} color={theme.colors.navyMuted} />
      </span>
    )
  );
  // ── ROW COMPOSITION v1 (part 7 fix loop, 2026-10-12, operator rulings from the composition
  // board) — three GROUPS, the same three on the list row and on the Archiv compact line:
  //   CLASSIFICATION (row 2 left): primary band label/badge · pending chip · secondary pills ·
  //     paperclip · the Neu-Analyse count (a note ON the classification, so it sits with it).
  //   INFO (row 3 left): the TENANT-EXCLUSIVE plum pill FIRST (sender pill on a sender-registry
  //     tenant; „Formular" on a form-extraction tenant; nothing yet elsewhere) · flags · Frist genannt ·
  //     Stufe n · the attribute pills (the utility exception, „Gas").
  //   BEARBEITUNG (row 3 right, marginLeft:auto): approved-as badge · Kopiert · Bearbeitet ·
  //     Eingang bestätigt.
  // The CLASSIFICATION group is a `display: contents` wrapper: it names the group (testids, order
  // guards) without adding a flex box, so each pill stays a direct flex item of its line.
  // The INFO group is a real box that GIVES WAY (part 7 fix loop, 2026-10-12, closing review): on a
  // narrow pane its tail clips (`flex: 0 1 auto; minWidth: 0; overflow: hidden`) while the
  // Bearbeitung group (`flexShrink: 0`, `marginLeft: auto`) stays whole — the decision is never cut
  // mid-word, and the info group's FIRST pills (the plum pill, the flags) are the ones that stay
  // visible. An empty info group is a zero-width box; the Bearbeitung group's auto margin absorbs
  // its gap, so nothing moves.
  const reanalysisTrace = rowPills.isVisible('reanalysis') && t.reanalysisCount != null && t.reanalysisCount > 0 && (
    <ClerkTrace kind="reanalysis" count={t.reanalysisCount} />
  );
  const classificationGroup = (
    <span data-testid="row-group-classification" style={{ display: 'contents' }}>
      {categoryBadge}
      {pendingChip}
      {secondaryAxisPills}
      {attachment}
      {reanalysisTrace}
    </span>
  );

  // THE sender pill, one gate for both branches: the clerk's „Absender" switch, the registry gate,
  // and something to state.
  const senderPill = rowPills.isVisible('sender_organisation') && senderRegistryServed && (t.senderOrganisation || t.senderKind) && (
    <SenderPill organisation={t.senderOrganisation ?? null} senderKind={t.senderKind ?? null} email={t.customerEmail ?? null} />
  );
  // THE „Formular" pill (part 7 fix loop, 2026-10-12): the clerk's „Formular" switch, the row's own
  // recognised form, and the SERVED form-extraction capability — the same gate as the Excel-batch
  // button (App's /config `formExtractionEnabled`, carried by the schema context). Never a role id.
  // While the capability is still unknown (warm load, /config not yet answered) a form-bearing row
  // RESERVES the pill's exact footprint (FormPill `reserved`, visibility: hidden), so the pill lands
  // in place and the info pills after it never move; if the capability lands off, the reservation
  // collapses (AttachmentGlyphSlot's pattern).
  const formPill = rowPills.isVisible('formular') && t.hasFormExtraction === true && (
    cm.capabilitiesLoading ? <FormPill reserved /> : cm.formExtractionEnabled && <FormPill />
  );
  const infoGroup = (
    <span data-testid="row-group-info" style={INFO_GROUP_STYLE}>
      {senderPill}
      {formPill}
      {flagPills}
      {rowPills.isVisible('frist_genannt') && <FristGenanntPill quote={t.deadlineQuote} />}
      {rowPills.isVisible('eskalationsstufe') && eskN && (
        <StufeChip n={eskN} variant="row" />
      )}
      {attributePills}
    </span>
  );

  // Row 3 right (small facts on a row, operator ruling 2026-10-19): the Bearbeitung traces as split
  // pills, then THE DECISION LAST — the approved-as badge or the declined-instead badge, one
  // component (DecisionBadge) in the chosen word's served hue.
  const bearbeitungGroup = (
    <span data-testid="row-group-bearbeitung" style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
      {rowPills.isVisible('kopiert') && showsKopiert({ ...t, claimVia: rowClaimVia(t) }) && <ClerkTrace kind="kopiert" />}
      {rowPills.isVisible('bearbeitet') && t.edited === true && <ClerkTrace kind="bearbeitet" />}
      {/* The RECEIPT's own signal (R31/R32, 2026-09-18). It reads `draftClaims` — the claim family —
          rather than a stamp of its own, because a receipt IS a claim. Deliberately NOT
          `copyApprovedAt`: core stopped stamping that for an ack copy, since a companion artifact is
          not a copy-approval. The WORD is the served `decision.receipt` face (ServedFace's
          three-state honesty); the tooltip is the copy toast's own sentence
          (`copyToastMessage('ack', face)`, constants.ts), read from where it is composed. */}
      {rowPills.isVisible('eingang_bestaetigt') && (t.draftClaims ?? []).includes('ack') && (
        <ClerkTrace kind="eingang_bestaetigt" title={copyToastMessage('ack', cm.face)} />
      )}
      {/* The yes badge (DecisionBadges: „Genehmigt", or „<Art> genehmigt" where kinds are stated).
          UNDATED on every row: the decision date lives on the detail's decision line. */}
      <DecisionBadges claims={approvedRowKinds(t)} primaryAxis={t.primaryAxis} />
      {/* The verdict (bottom-bar decision, 2026-10-16; the badge since 2026-10-19): what the clerk
          did INSTEAD of the suggestion, the same word and hue the detail's decision line shows
          (declineInsteadWord / declineInsteadHue — one home). A decline without a word: the key
          segment alone. */}
      {t.decline && (
        <DecisionBadge
          act="instead"
          hue={declineInsteadHue(cm, t.decline)}
          word={declineInsteadWord(cm, t.decline)}
          label={DECLINE_LABEL}
          testId="decline-pill"
        />
      )}
    </span>
  );

  // Compact (Archiv ledger): ONE line carries the three groups in the same order — the row-2/row-3
  // split cannot be drawn there; the date lives on the caller's subject line.
  if (compact) {
    return (
      <div data-testid="row-line-compact" style={{ display: 'flex', alignItems: 'center', marginTop: 3, minHeight: 18, overflow: 'hidden', gap: 4 }}>
        {/* The decision never clips (B5 walk, 2026-10-27: the Archiv row cut a decline badge to
            „St"). The classification and the info pills share ONE lane that may shrink and fades at
            its edge; the Bearbeitung group after it keeps its full width. */}
        <span data-testid="row-line-compact-lane" style={{
          display: 'flex', alignItems: 'center', gap: 4, flex: '1 1 auto', minWidth: 0, overflow: 'hidden',
          maskImage: 'linear-gradient(to right, black calc(100% - 14px), transparent)',
          WebkitMaskImage: 'linear-gradient(to right, black calc(100% - 14px), transparent)',
        }}>
          {classificationGroup}
          {infoGroup}
        </span>
        {bearbeitungGroup}
      </div>
    );
  }

  return (
    <>
      {/* Row 2 — what the mail IS: the classification and its notes. */}
      <div data-testid="row-line-2" style={{ display: 'flex', alignItems: 'center', marginTop: 3, minHeight: 18, overflow: 'hidden', gap: 4 }}>
        {classificationGroup}
      </div>
      {/* Row 3 — the info pills left, the Bearbeitung right. PRE-ALLOCATED: it renders on every row
          at row 2's own 18px minHeight (every pill here is at most 18px tall: the split pills and the
          decision badge are exactly 18, the outlined pills less), so a row with no plum
          pill, no info pill and no Bearbeitung stands exactly as tall as a full one and nothing
          below it moves when a group fills (UI Layout Stability). */}
      <div data-testid="row-line-3" style={{ display: 'flex', alignItems: 'center', marginTop: 3, minHeight: 18, overflow: 'hidden', gap: 4 }}>
        {infoGroup}
        {bearbeitungGroup}
      </div>
    </>
  );
};

// Row 3's / the compact line's INFO group box — see TicketRowMeta's ROW COMPOSITION note. Shrinks
// (minWidth 0) and clips its own tail; never wraps (the line is one row high).
const INFO_GROUP_STYLE: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 4,
  flex: '0 1 auto', minWidth: 0, overflow: 'hidden',
};
